import React, { useState, useEffect } from "react";
import { playTripOfferChime } from "../utils/audio";

export default function TripOffer({ offer, onAccept, onDecline }) {
  const [timeLeft, setTimeLeft] = useState(offer.timeout_seconds || 60);

  // Repeat chime audio and vibration every 5s (capped at 3 replays)
  useEffect(() => {
    let playCount = 0;
    const interval = setInterval(() => {
      if (playCount >= 3) {
        clearInterval(interval);
        return;
      }
      playCount++;
      if (navigator.vibrate) navigator.vibrate([200, 100, 200, 100, 200]);
      playTripOfferChime();
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          onDecline(offer.order_id);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [offer, onDecline]);

  return (
    <div className="fixed inset-0 bg-blue-600 z-50 flex flex-col">
      {/* Timer bar */}
      <div className="h-1.5 bg-blue-800">
        <div
          className="h-full bg-yellow-400 transition-all duration-1000"
          style={{
            width: `${(timeLeft / (offer.timeout_seconds || 60)) * 100}%`,
          }}
        />
      </div>

      <div className="flex-1 flex flex-col px-6 py-8 overflow-y-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3 animate-pulse">
            <svg
              className="w-8 h-8 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-white">
            New Delivery Request!
          </h1>
          <p className="text-blue-200 text-lg mt-1">{timeLeft}s to respond</p>
        </div>

        {/* Trip Details */}
        <div className="bg-white rounded-2xl p-5 space-y-4 flex-1">
          <div>
            <p className="text-xs text-gray-500 uppercase font-semibold">
              Pickup
            </p>
            <p className="text-gray-900 font-medium mt-0.5">
              {offer.seller_name}
            </p>
            <p className="text-gray-600 text-sm">{offer.pickup_address}</p>
          </div>
          <div className="border-l-2 border-dashed border-gray-300 ml-2 h-4" />
          <div>
            <p className="text-xs text-gray-500 uppercase font-semibold">
              Drop-off
            </p>
            <p className="text-gray-900 font-medium mt-0.5">
              {offer.buyer_name}
            </p>
            <p className="text-gray-600 text-sm">{offer.dropoff_address}</p>
          </div>

          <div className="grid grid-cols-2 gap-3 pt-2">
            <div className="bg-gray-50 rounded-xl p-3 text-center">
              <p className="text-xs text-gray-500">Distance</p>
              <p className="text-lg font-bold text-gray-900">
                {offer.distance_km} km
              </p>
            </div>
            <div className="bg-blue-50 rounded-xl p-3 text-center">
              <p className="text-xs text-blue-600">Your earnings</p>
              <p className="text-lg font-bold text-blue-700">
                ${(offer.estimated_earnings || 0.5).toFixed(2)}
              </p>
            </div>
          </div>

          {/* Badges */}
          <div className="flex gap-2 flex-wrap">
            {offer.express && (
              <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
                Express
              </span>
            )}
            {offer.cod_enabled && (
              <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm font-medium">
                COD: Collect ${(offer.cod_amount || 0).toFixed(2)}
              </span>
            )}
          </div>

          {offer.item_description && (
            <div>
              <p className="text-xs text-gray-500">Item</p>
              <p className="text-sm text-gray-700">{offer.item_description}</p>
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="px-6 pb-8 pt-4 grid grid-cols-2 gap-4">
        <button
          onClick={() => onDecline(offer.order_id)}
          className="py-4 rounded-2xl bg-red-500 text-white font-bold text-lg hover:bg-red-600 active:scale-95 transition-all"
        >
          DECLINE
        </button>
        <button
          onClick={() => onAccept(offer.order_id)}
          className="py-4 rounded-2xl bg-green-500 text-white font-bold text-lg hover:bg-green-600 active:scale-95 transition-all shadow-lg shadow-green-500/30"
        >
          ACCEPT
        </button>
      </div>
    </div>
  );
}
